// src/pages/portal/MarketingPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PortalLayout from './PortalLayout';
import { marketingService } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import useIsMobile from '../../hooks/useIsMobile';
import { C } from '../../styles/theme';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
    getToday, getStartOfWeek, getEndOfWeek, getStartOfMonth, getStartOfYear,
    EmptyState, SectionDivider, formatDateLabel,
} from './reporting/_shared';
import PromotionsTab from './marketing/PromotionsTab';

// ─── Brand tokens ─────────────────────────────────────────────────────────────
const FONT = "'Plus Jakarta Sans', sans-serif";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtNum = (v) => Number(v || 0).toLocaleString();

const PLATFORM_META = {
    instagram: { labelKey: 'instagram', color: C.pink },
    facebook:  { labelKey: 'facebook',  color: C.blue },
    google:    { labelKey: 'google_business_profile', color: '#ea4335' },
};

const STAR_VALUE = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };

// KPI metric keys we surface per platform (in display order)
const KPI_METRICS = [
    { key: 'reach',              labelKey: 'reach' },
    { key: 'views',             labelKey: 'views' },
    { key: 'followers',         labelKey: 'followers' },
    { key: 'total_interactions',labelKey: 'total_interactions' },
    { key: 'profile_views',     labelKey: 'profile_views' },
];

// Maps Graph API media_type enums to translation keys (safe fallback to raw value)
const MEDIA_TYPE_KEYS = { IMAGE:'media_image', VIDEO:'media_video', CAROUSEL_ALBUM:'media_carousel', REELS:'media_reel', STORY:'media_story', FEED:'media_post', POST:'media_post', post:'media_post' };
const mediaTypeLabel = (mt, t) => { const k = MEDIA_TYPE_KEYS[mt] || MEDIA_TYPE_KEYS[String(mt||'').toUpperCase()]; return k ? t(k) : (mt || ''); };

// ─── Toast ────────────────────────────────────────────────────────────────────
const ToastContainer = ({ toasts }) => (
    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
        {toasts.map(t => (
            <div key={t.id} style={{
                padding: '10px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600,
                background: t.type === 'success' ? '#eff5ff' : C.redLight,
                border: `1.5px solid ${t.type === 'success' ? '#86efac' : C.redBorder}`,
                color: t.type === 'success' ? '#16a34a' : C.red,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                fontFamily: FONT, pointerEvents: 'auto', minWidth: 220,
            }}>
                {t.message}
            </div>
        ))}
    </div>
);

// ─── Section header ───────────────────────────────────────────────────────────
const SectionHeader = ({ title, description }) => (
    <div style={{ marginBottom: 28, paddingBottom: 16, borderBottom: `1.5px solid ${C.border}` }}>
        <h2 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 800, color: C.text, margin: 0 }}>{title}</h2>
        {description && (
            <p style={{ fontFamily: FONT, fontSize: 13, color: C.muted, margin: '6px 0 0', lineHeight: 1.55 }}>
                {description}
            </p>
        )}
    </div>
);

// ─── Meta logo glyph ──────────────────────────────────────────────────────────
const MetaIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12c0-3 1.5-6 4-6 2 0 3 2 4 4s2.5 5 4.5 5 3.5-3 3.5-5-1-4-3-4" />
    </svg>
);

const IconLink = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
);

const ExternalIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
    </svg>
);

const GoogleIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
);

// Map connection status → badge styling + label key
const badgeFor = (status, t) => {
    if (status === 'connected') {
        return { bg: C.greenLight, color: C.greenDark, border: C.greenBorder, label: t('connected') };
    }
    if (status === 'expired') {
        return { bg: C.amberLight, color: C.amber, border: C.amberBorder, label: t('connection_expired') };
    }
    if (status === 'error') {
        return { bg: C.redLight, color: C.red, border: C.redBorder, label: t('connection_error_status') };
    }
    if (status === 'pending_location') {
        return { bg: C.amberLight, color: C.amber, border: C.amberBorder, label: t('setup_incomplete') };
    }
    return { bg: C.bg, color: C.muted, border: C.border, label: t('not_connected') };
};

// ─── KPI tile ─────────────────────────────────────────────────────────────────
const KpiTile = ({ label, value, change, invert, t }) => {
    const hasChange = change != null && !Number.isNaN(change);
    const positive = invert ? change <= 0 : change >= 0;
    const changeColor = positive ? C.green : C.red;
    const arrow = change >= 0 ? '↑' : '↓';
    return (
        <div style={{
            background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 12,
            padding: '16px 18px', minWidth: 0,
        }}>
            <div style={{
                fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: 0.8, color: C.muted, marginBottom: 8,
            }}>{label}</div>
            <div style={{ fontFamily: FONT, fontSize: 24, fontWeight: 800, color: C.text, marginBottom: 6 }}>{value}</div>
            {hasChange ? (
                <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: changeColor }}>
                    {arrow} {Math.abs(change).toFixed(1)}% {t('vs_prev_period')}
                </div>
            ) : (
                <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: C.mutedLight }}>
                    {t('no_change_data')}
                </div>
            )}
        </div>
    );
};

// ─── Chart card ───────────────────────────────────────────────────────────────
const ChartCard = ({ title, children }) => (
    <div style={{ background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
        <div style={{
            fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 0.8, color: C.muted, marginBottom: 16,
        }}>{title}</div>
        {children}
    </div>
);

const xTick = (v) => (typeof v === 'string' && v.length >= 10 ? v.slice(5) : String(v));

// ─── Platform section ─────────────────────────────────────────────────────────
const PlatformSection = ({ platformKey, platformData, kpis, isMobile, t }) => {
    const meta = PLATFORM_META[platformKey];
    const accent = meta ? meta.color : C.green;
    const trend = (platformData && Array.isArray(platformData.trend)) ? platformData.trend : [];

    // Match KPIs by stable fields (platform + metric), not translated labels.
    const platformLabel = t(meta.labelKey);
    const findKpi = useCallback((metricKey) => {
        if (!Array.isArray(kpis)) return null;
        return kpis.find(k =>
            String(k.platform).toLowerCase() === platformKey && k.metric === metricKey
        ) || null;
    }, [kpis, platformKey]);

    const hasReachOrViews = trend.some(r => Number(r.reach || 0) > 0 || Number(r.views || 0) > 0);
    const hasFollowers = trend.some(r => Number(r.followers || 0) > 0);

    return (
        <div style={{ marginBottom: 32 }}>
            {/* Platform heading */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: accent, flexShrink: 0 }} />
                <h3 style={{ fontFamily: FONT, fontSize: 16, fontWeight: 800, color: C.text, margin: 0 }}>
                    {platformLabel}
                </h3>
            </div>

            {/* KPI tiles */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : `repeat(${KPI_METRICS.length}, 1fr)`,
                gap: 12, marginBottom: 20,
            }}>
                {KPI_METRICS.map((m) => {
                    const metricLabel = t(m.labelKey);
                    const k = findKpi(m.key);
                    return (
                        <KpiTile
                            key={m.key}
                            label={metricLabel}
                            value={fmtNum(k ? k.value : 0)}
                            change={k ? k.change_percent : null}
                            invert={k ? !!k.invert : false}
                            t={t}
                        />
                    );
                })}
            </div>

            {/* Charts */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: 16 }}>
                <ChartCard title={t('reach_views_trend')}>
                    {hasReachOrViews ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={trend} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                                <XAxis dataKey="metric_date" tickFormatter={xTick} tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                                <Tooltip
                                    contentStyle={{ fontFamily: FONT, fontSize: 12, borderRadius: 8, border: `1px solid ${C.border}` }}
                                    formatter={(v, name) => [fmtNum(v), name === 'reach' ? t('reach') : t('views')]}
                                />
                                <Bar dataKey="reach" radius={[4, 4, 0, 0]} fill={accent} />
                                <Bar dataKey="views" radius={[4, 4, 0, 0]}>
                                    {trend.map((entry, i) => (
                                        <Cell key={`v-${i}`} fill={C.greenBorder} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <EmptyState title={t('no_insights_data')} />
                    )}
                </ChartCard>

                <ChartCard title={t('followers_trend')}>
                    {hasFollowers ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                <XAxis dataKey="metric_date" tickFormatter={xTick} tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} domain={['auto', 'auto']} />
                                <Tooltip
                                    contentStyle={{ fontFamily: FONT, fontSize: 12, borderRadius: 8, border: `1px solid ${C.border}` }}
                                    formatter={(v) => [fmtNum(v), t('followers')]}
                                />
                                <Line type="monotone" dataKey="followers" stroke={accent} strokeWidth={2} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <EmptyState title={t('no_insights_data')} />
                    )}
                </ChartCard>
            </div>
        </div>
    );
};

// ─── Top posts ────────────────────────────────────────────────────────────────
const truncate = (s, n) => {
    if (!s) return '';
    return s.length > n ? s.slice(0, n).trimEnd() + '…' : s;
};

const TopPosts = ({ posts, isMobile, t }) => {
    if (!posts || posts.length === 0) {
        return <EmptyState title={t('no_posts_data')} />;
    }

    if (isMobile) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {posts.map((p) => (
                    <div key={`${p.platform}-${p.post_id}`} style={{
                        background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 12, padding: 14,
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                            <span style={{
                                fontFamily: FONT, fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                                letterSpacing: 0.6, color: (PLATFORM_META[p.platform] && PLATFORM_META[p.platform].color) || C.muted,
                            }}>
                                {PLATFORM_META[p.platform] ? t(PLATFORM_META[p.platform].labelKey) : p.platform}
                                {p.media_type ? ` · ${mediaTypeLabel(p.media_type, t)}` : ''}
                            </span>
                            {p.permalink && (
                                <a href={p.permalink} target="_blank" rel="noopener noreferrer"
                                    style={{ color: C.green, display: 'inline-flex', alignItems: 'center' }} title={t('open_post')}>
                                    <ExternalIcon />
                                </a>
                            )}
                        </div>
                        <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 10, lineHeight: 1.4 }}>
                            {truncate(p.caption, 80) || t('no_caption')}
                        </div>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>
                                {t('views')}: <b style={{ color: C.text }}>{fmtNum(p.views)}</b>
                            </span>
                            <span style={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>
                                {t('interactions')}: <b style={{ color: C.text }}>{fmtNum(p.total_interactions)}</b>
                            </span>
                            <span style={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>
                                {t('engagement_rate')}: <b style={{ color: C.text }}>{((p.engagement_rate || 0) * 100).toFixed(1)}%</b>
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    const cols = '110px 1fr 100px 110px 130px 44px';
    const headerStyle = {
        fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.8, color: C.muted, padding: '8px 12px', borderBottom: `1.5px solid ${C.border}`,
    };
    const cellStyle = {
        fontFamily: FONT, fontSize: 13, color: C.text, padding: '10px 12px',
        borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center',
    };
    return (
        <div style={{ display: 'grid', gridTemplateColumns: cols }}>
            <div style={headerStyle}>{t('platform')}</div>
            <div style={headerStyle}>{t('caption')}</div>
            <div style={headerStyle}>{t('views')}</div>
            <div style={headerStyle}>{t('interactions')}</div>
            <div style={headerStyle}>{t('engagement_rate')}</div>
            <div style={headerStyle}></div>
            {posts.map((p) => (
                <React.Fragment key={`${p.platform}-${p.post_id}`}>
                    <div style={cellStyle}>
                        <span style={{
                            fontFamily: FONT, fontSize: 11, fontWeight: 700,
                            color: (PLATFORM_META[p.platform] && PLATFORM_META[p.platform].color) || C.muted,
                        }}>
                            {PLATFORM_META[p.platform] ? t(PLATFORM_META[p.platform].labelKey) : p.platform}
                        </span>
                    </div>
                    <div style={{ ...cellStyle, fontWeight: 600, flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                        <span>{truncate(p.caption, 70) || t('no_caption')}</span>
                        {p.media_type && (
                            <span style={{ fontSize: 11, color: C.mutedLight, fontWeight: 600 }}>{mediaTypeLabel(p.media_type, t)}</span>
                        )}
                    </div>
                    <div style={cellStyle}>{fmtNum(p.views)}</div>
                    <div style={cellStyle}>{fmtNum(p.total_interactions)}</div>
                    <div style={cellStyle}>{((p.engagement_rate || 0) * 100).toFixed(1)}%</div>
                    <div style={cellStyle}>
                        {p.permalink && (
                            <a href={p.permalink} target="_blank" rel="noopener noreferrer"
                                style={{ color: C.green, display: 'inline-flex', alignItems: 'center' }} title={t('open_post')}>
                                <ExternalIcon />
                            </a>
                        )}
                    </div>
                </React.Fragment>
            ))}
        </div>
    );
};

// ─── GBP Section ──────────────────────────────────────────────────────────────
const GbpSection = ({ discovery, reviews, keywords, isMobile, t }) => {
    const accent = PLATFORM_META.google.color;
    const trend = (discovery && Array.isArray(discovery.trend)) ? discovery.trend : [];
    const kpis = (discovery && discovery.kpis) ? discovery.kpis : {};
    const hasData = trend.length > 0;

    const reviewsList = (reviews && Array.isArray(reviews.reviews)) ? reviews.reviews : [];
    const summary = (reviews && reviews.summary) || {};
    const avgRating = summary.average_rating ? Number(summary.average_rating) : 0;
    const totalReviews = summary.total_review_count ? Number(summary.total_review_count) : 0;
    const starDist = summary; // star_1..star_5 live directly on summary
    const kwList = (keywords && Array.isArray(keywords.keywords)) ? keywords.keywords : [];

    const gbpKpiMetrics = [
        { key: 'total_impressions',  labelKey: 'total_impressions'  },
        { key: 'call_clicks',        labelKey: 'call_clicks'        },
        { key: 'website_clicks',     labelKey: 'website_clicks'     },
        { key: 'direction_requests', labelKey: 'direction_requests' },
    ];

    return (
        <div style={{ marginBottom: 32 }}>
            <SectionDivider label={t('local_seo')} tag={t('google_business_profile')} tagColor={PLATFORM_META.google.color} tagBg="rgba(234,67,53,0.08)" />

            {!hasData ? (
                <EmptyState title={t('gbp_no_data_yet')} />
            ) : (
                <>
                    {/* KPI tiles */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                        gap: 12, marginBottom: 20,
                    }}>
                        {gbpKpiMetrics.map((m) => (
                            <KpiTile
                                key={m.key}
                                label={t(m.labelKey)}
                                value={fmtNum(kpis[m.key] || 0)}
                                change={null}
                                invert={false}
                                t={t}
                            />
                        ))}
                    </div>

                    {/* Trend chart */}
                    <ChartCard title={t('total_impressions')}>
                        <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                <XAxis dataKey="metric_date" tickFormatter={(v) => formatDateLabel(v)} tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                                <Tooltip
                                    contentStyle={{ fontFamily: FONT, fontSize: 12, borderRadius: 8, border: `1px solid ${C.border}` }}
                                    formatter={(v) => [fmtNum(v), t('total_impressions')]}
                                />
                                <Line type="monotone" dataKey="total_impressions" stroke={accent} strokeWidth={2} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    {/* Reviews */}
                    <div style={{ marginTop: 20 }}>
                        <ChartCard title={t('recent_reviews')}>
                            <div style={{ display: 'flex', gap: 32, marginBottom: 16, flexWrap: 'wrap' }}>
                                <div>
                                    <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 4 }}>{t('average_rating')}</div>
                                    <div style={{ fontFamily: FONT, fontSize: 28, fontWeight: 800, color: C.text }}>{avgRating.toFixed(1)} ★</div>
                                </div>
                                <div>
                                    <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 4 }}>{t('total_reviews')}</div>
                                    <div style={{ fontFamily: FONT, fontSize: 28, fontWeight: 800, color: C.text }}>{fmtNum(totalReviews)}</div>
                                </div>
                            </div>

                            {/* Star distribution */}
                            {[5, 4, 3, 2, 1].map((star) => {
                                const count = Number(starDist[`star_${star}`] || 0);
                                const pct = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                                return (
                                    <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                        <span style={{ fontFamily: FONT, fontSize: 12, color: C.muted, width: 12, textAlign: 'right' }}>{star}</span>
                                        <span style={{ fontSize: 11, color: C.amber }}>★</span>
                                        <div style={{ flex: 1, height: 8, background: C.bg, borderRadius: 4, overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${pct}%`, background: accent, borderRadius: 4, transition: 'width 0.3s' }} />
                                        </div>
                                        <span style={{ fontFamily: FONT, fontSize: 12, color: C.muted, width: 28, textAlign: 'right' }}>{count}</span>
                                    </div>
                                );
                            })}

                            {/* Reviews list */}
                            {reviewsList.length > 0 && (
                                <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {reviewsList.map((rv, i) => (
                                        <div key={rv.review_id ?? i} style={{ padding: '12px 14px', background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.text }}>{rv.reviewer_name}</span>
                                                <span style={{ fontFamily: FONT, fontSize: 12, color: C.amber }}>{'★'.repeat(STAR_VALUE[rv.star_rating] || 0)}</span>
                                            </div>
                                            {rv.comment && (
                                                <p style={{ fontFamily: FONT, fontSize: 13, color: C.text, margin: '0 0 6px', lineHeight: 1.5 }}>{rv.comment}</p>
                                            )}
                                            {rv.reply_comment && (
                                                <div style={{ background: C.greenLight, border: `1px solid ${C.greenBorder}`, borderRadius: 8, padding: '8px 12px', marginTop: 8 }}>
                                                    <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: C.greenDark, marginBottom: 4 }}>{t('owner_reply')}</div>
                                                    <p style={{ fontFamily: FONT, fontSize: 12, color: C.text, margin: 0, lineHeight: 1.5 }}>{rv.reply_comment}</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </ChartCard>
                    </div>

                    {/* Top keywords */}
                    {kwList.length > 0 && (
                        <div style={{ marginTop: 20 }}>
                            <ChartCard title={t('top_search_keywords')}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px' }}>
                                    <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, padding: '8px 12px', borderBottom: `1.5px solid ${C.border}` }}>
                                        {t('top_search_keywords')}
                                    </div>
                                    <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, padding: '8px 12px', borderBottom: `1.5px solid ${C.border}`, textAlign: 'right' }}>
                                        {t('impressions')}
                                    </div>
                                    {kwList.map((kw, i) => (
                                        <React.Fragment key={kw.keyword ?? i}>
                                            <div style={{ fontFamily: FONT, fontSize: 13, color: C.text, padding: '10px 12px', borderBottom: `1px solid ${C.border}` }}>
                                                {kw.keyword}
                                            </div>
                                            <div style={{ fontFamily: FONT, fontSize: 13, color: C.text, padding: '10px 12px', borderBottom: `1px solid ${C.border}`, textAlign: 'right' }}>
                                                {fmtNum(kw.impressions)}
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </div>
                            </ChartCard>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// ─── Date range picker ────────────────────────────────────────────────────────
const getPresets = (t) => [
    { key: 'today', label: t('today') },
    { key: 'week',  label: t('this_week') },
    { key: 'month', label: t('this_month') },
    { key: 'year',  label: t('this_year') },
    { key: 'custom', label: t('custom') },
];

const DateRangePicker = ({ preset, setPreset, startDate, setStartDate, endDate, setEndDate, t, isMobile }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
        <div style={{
            display: 'flex', background: C.bg, borderRadius: 9, border: `1.5px solid ${C.border}`,
            overflow: 'hidden', width: isMobile ? '100%' : 'auto',
        }}>
            {getPresets(t).map((p) => (
                <button
                    key={p.key}
                    onClick={() => setPreset(p.key)}
                    style={{
                        fontFamily: FONT, fontSize: isMobile ? 13 : 12, fontWeight: 600,
                        padding: isMobile ? '9px 6px' : '7px 14px', border: 'none', cursor: 'pointer',
                        flex: isMobile ? 1 : 'initial',
                        background: preset === p.key ? C.green : 'transparent',
                        color: preset === p.key ? C.white : C.muted,
                        transition: 'all 0.15s', whiteSpace: 'nowrap',
                    }}
                >{p.label}</button>
            ))}
        </div>
        {preset === 'custom' && (
            <React.Fragment>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    style={{ fontFamily: FONT, fontSize: 12, padding: '6px 10px', border: `1.5px solid ${C.border}`, borderRadius: 8, outline: 'none', flex: isMobile ? 1 : 'initial', minWidth: 0 }} />
                <span style={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>{t('to')}</span>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    style={{ fontFamily: FONT, fontSize: 12, padding: '6px 10px', border: `1.5px solid ${C.border}`, borderRadius: 8, outline: 'none', flex: isMobile ? 1 : 'initial', minWidth: 0 }} />
            </React.Fragment>
        )}
    </div>
);

const MarketingPage = () => {
    const { t } = useLanguage();
    const isMobile = useIsMobile();

    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [busy, setBusy] = useState(false);
    const [toasts, setToasts] = useState([]);

    // Primary section: 'social' | 'promotions'
    const [section, setSection] = useState('social');

    // View: 'insights' | 'connections'
    const [view, setView] = useState('insights');
    const [viewInitialized, setViewInitialized] = useState(false);

    // Google location picker state
    const [googleLocations, setGoogleLocations] = useState(null); // null=closed, []=none, [...]=options
    const [googleQuotaBlocked, setGoogleQuotaBlocked] = useState(false);

    // GBP insights state
    const [gbpDiscovery, setGbpDiscovery] = useState(null);
    const [gbpReviews, setGbpReviews] = useState(null);
    const [gbpKeywords, setGbpKeywords] = useState(null);

    // Insights state
    const [preset, setPreset] = useState('month');
    const [customStart, setCustomStart] = useState(getToday);
    const [customEnd, setCustomEnd] = useState(getToday);
    const [insights, setInsights] = useState(null);
    const [topPosts, setTopPosts] = useState([]);
    const [insightsLoading, setInsightsLoading] = useState(false);

    const addToast = useCallback((type, message) => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, type, message }]);
        setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), 3500);
    }, []);

    const loadConnections = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const data = await marketingService.getConnections();
            const list = Array.isArray(data) ? data : [];
            setConnections(list);
            // Default to Insights when there is ≥1 connection, else Connections.
            setViewInitialized(prev => {
                if (!prev) {
                    setView(list.length > 0 ? 'insights' : 'connections');
                    return true;
                }
                return prev;
            });
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    // Handle OAuth return query params, then load connections
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('connected') === '1') {
            addToast('success', t('connection_success'));
            // Feature A: the connect callback kicked off an ETL refresh server-side.
            if (params.get('refreshing') === '1') {
                setRefreshing(true);
                pollRefreshStatus(0);
            }
        } else if (params.get('error')) {
            addToast('error', t('connection_error'));
        }
        if (params.has('connected') || params.has('error')) {
            window.history.replaceState({}, document.title, window.location.pathname);
        }
        loadConnections();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Effective date range
    const dateRange = useMemo(() => {
        switch (preset) {
            case 'today': return { start: getToday(), end: getToday() };
            case 'week':  return { start: getStartOfWeek(), end: getEndOfWeek() };
            case 'month': return { start: getStartOfMonth(), end: getToday() };
            case 'year':  return { start: getStartOfYear(), end: getToday() };
            case 'custom': return { start: customStart, end: customEnd };
            default: return { start: getStartOfMonth(), end: getToday() };
        }
    }, [preset, customStart, customEnd]);

    const fetchInsights = useCallback(async () => {
        setInsightsLoading(true);
        try {
            const [ins, posts] = await Promise.all([
                marketingService.getInsights(dateRange.start, dateRange.end),
                marketingService.getTopPosts(dateRange.start, dateRange.end, null, 10),
            ]);
            setInsights(ins || null);
            setTopPosts((posts && Array.isArray(posts.posts)) ? posts.posts : []);
        } catch (e) {
            setInsights(null);
            setTopPosts([]);
        } finally {
            setInsightsLoading(false);
        }
    }, [dateRange.start, dateRange.end]);

    // Fetch insights when in insights view + dates change
    useEffect(() => {
        if (view === 'insights' && connections.length > 0) {
            fetchInsights();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, fetchInsights, connections.length]);

    // ── Data refresh (on-demand + on-connect) ──────────────────────────────
    const [refreshing, setRefreshing] = useState(false);
    const refreshPollRef = useRef(null);
    const isMountedRef = useRef(true);
    useEffect(() => () => { isMountedRef.current = false; }, []);

    const stopRefreshPolling = useCallback(() => {
        if (refreshPollRef.current) {
            clearTimeout(refreshPollRef.current);
            refreshPollRef.current = null;
        }
    }, []);

    // Poll the backend until the ETL refresh finishes (or we hit the cap),
    // then reload insights. ~2 min cap at 2s intervals. Guarded against the
    // component unmounting mid-poll (the in-flight request can't be cancelled,
    // so we bail before scheduling the next tick or touching state).
    const pollRefreshStatus = useCallback((attempt = 0) => {
        const MAX_ATTEMPTS = 60;
        refreshPollRef.current = setTimeout(async () => {
            if (!isMountedRef.current) return;
            try {
                const st = await marketingService.getRefreshStatus();
                if (!isMountedRef.current) return;
                if (st.status === 'running' && attempt < MAX_ATTEMPTS) {
                    pollRefreshStatus(attempt + 1);
                    return;
                }
                stopRefreshPolling();
                setRefreshing(false);
                if (st.status === 'error') {
                    addToast('error', t('refresh_error'));
                } else if (st.status === 'done') {
                    addToast('success', t('refresh_complete'));
                }
                await fetchInsights();
            } catch (e) {
                if (!isMountedRef.current) return;
                stopRefreshPolling();
                setRefreshing(false);
            }
        }, 2000);
    }, [stopRefreshPolling, addToast, t, fetchInsights]);

    const handleRefresh = useCallback(async () => {
        if (refreshing) return;
        setRefreshing(true);
        try {
            await marketingService.refresh();
            pollRefreshStatus(0);
        } catch (e) {
            setRefreshing(false);
            addToast('error', t('refresh_error'));
        }
    }, [refreshing, pollRefreshStatus, addToast, t]);

    // Clean up any in-flight poll on unmount
    useEffect(() => stopRefreshPolling, [stopRefreshPolling]);

    // Open location picker when Google OAuth returns with ?google_pending=1
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('google_pending') !== '1') return;
        window.history.replaceState({}, document.title, window.location.pathname);
        openGooglePicker();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Escape key dismisses location picker
    useEffect(() => {
        if (googleLocations === null) return;
        const onKey = (e) => { if (e.key === 'Escape') setGoogleLocations(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [googleLocations]);

    // Load GBP data when a google_gbp connection is active
    const hasGoogle = connections.some((c) => c.platform === 'google_gbp' && c.status === 'connected');
    useEffect(() => {
        if (!hasGoogle) return;
        (async () => {
            try {
                const [disc, revs, kws] = await Promise.all([
                    marketingService.getLocalDiscovery(dateRange.start, dateRange.end),
                    marketingService.getReviews(50, 0),
                    marketingService.getSearchKeywords(null, 25),
                ]);
                setGbpDiscovery(disc);
                setGbpReviews(revs);
                setGbpKeywords(kws);
            } catch (e) {
                // silently fail — section shows empty state
            }
        })();
    }, [hasGoogle, dateRange.start, dateRange.end]);

    const handleConnect = async () => {
        setBusy(true);
        try {
            const url = await marketingService.getMetaAuthorizeUrl();
            window.location.href = url;
        } catch (e) {
            addToast('error', t('connection_error'));
            setBusy(false);
        }
    };

    const handleGoogleConnect = async () => {
        setBusy(true);
        try {
            const url = await marketingService.getGoogleAuthorizeUrl();
            window.location.href = url;
        } catch (e) {
            addToast('error', t('connection_error'));
            setBusy(false);
        }
    };

    // Open the location picker for a tenant that has finished OAuth but not yet
    // chosen a location (status='pending_location'), or returns from OAuth.
    const openGooglePicker = async () => {
        try {
            const data = await marketingService.getGoogleLocations();
            setGoogleLocations(data.locations || []);
            setGoogleQuotaBlocked(!!data.quota_blocked);
        } catch (e) {
            addToast('error', t('connection_error'));
        }
    };

    const handleSelectLocation = async (loc) => {
        try {
            await marketingService.selectGoogleLocation(loc.location_id, loc.account_id, loc.title);
            setGoogleLocations(null);
            const conns = await marketingService.getConnections();
            setConnections(Array.isArray(conns) ? conns : []);
            addToast('success', t('connection_success'));
        } catch (e) {
            addToast('error', t('connection_error'));
        }
    };

    const handleDisconnect = async (platform, externalAccountId) => {
        setBusy(true);
        try {
            await marketingService.disconnect(platform, externalAccountId);
            addToast('success', t('disconnected_success'));
            await loadConnections();
        } catch (e) {
            addToast('error', t('disconnect_error'));
        } finally {
            setBusy(false);
        }
    };

    // Find the Meta rows
    const facebook = connections.find(c => c.platform === 'meta_facebook');
    const instagram = connections.find(c => c.platform === 'meta_instagram');
    const metaConnected = !!facebook;
    const metaStatus = facebook ? (facebook.status || 'connected') : 'none';
    const needsReconnect = metaStatus === 'expired' || metaStatus === 'error';
    const lastError = facebook && facebook.last_error;

    const primaryBtnStyle = {
        padding: '10px 22px', borderRadius: 9, border: 'none',
        background: needsReconnect ? C.amber : C.green, color: C.white,
        cursor: busy ? 'not-allowed' : 'pointer',
        fontFamily: FONT, fontSize: 13, fontWeight: 700,
        opacity: busy ? 0.6 : 1, transition: 'opacity 0.15s',
    };

    const ghostBtnStyle = {
        padding: '10px 18px', borderRadius: 9,
        border: `1.5px solid ${C.border}`, background: C.white, color: C.muted,
        cursor: busy ? 'not-allowed' : 'pointer',
        fontFamily: FONT, fontSize: 13, fontWeight: 700,
        opacity: busy ? 0.6 : 1, transition: 'opacity 0.15s',
    };

    const renderMetaCard = () => {
        const badge = badgeFor(metaStatus, t);
        return (
            <div style={{
                background: C.white, border: `1px solid ${C.border}`, borderRadius: 14,
                padding: isMobile ? 20 : 24, maxWidth: 720,
            }}>
                <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 16,
                    flexDirection: isMobile ? 'column' : 'row',
                }}>
                    <div style={{
                        width: 48, height: 48, borderRadius: 12, flexShrink: 0,
                        background: 'linear-gradient(135deg, #1877f2, #0a5dc2)', color: C.white,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <MetaIcon />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
                            <span style={{ fontFamily: FONT, fontSize: 16, fontWeight: 800, color: C.text }}>
                                {t('meta_facebook_instagram')}
                            </span>
                            <span style={{
                                padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                                fontFamily: FONT, background: badge.bg, color: badge.color,
                                border: `1px solid ${badge.border}`,
                            }}>
                                {badge.label}
                            </span>
                        </div>
                        <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, lineHeight: 1.55, marginBottom: 12 }}>
                            {t('connect_meta_desc')}
                        </div>

                        {metaConnected && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                                {facebook && facebook.display_name && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: FONT, fontSize: 13, color: C.text }}>
                                        <span style={{ fontWeight: 700, color: C.muted, minWidth: 84 }}>{t('facebook_page')}</span>
                                        <span style={{ fontWeight: 600 }}>{facebook.display_name}</span>
                                    </div>
                                )}
                                {instagram && instagram.display_name && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: FONT, fontSize: 13, color: C.text }}>
                                        <span style={{ fontWeight: 700, color: C.muted, minWidth: 84 }}>{t('instagram_account')}</span>
                                        <span style={{ fontWeight: 600 }}>{instagram.display_name}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {needsReconnect && lastError && (
                            <div style={{
                                background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 8,
                                padding: '8px 12px', fontFamily: FONT, fontSize: 12, color: C.red,
                                marginBottom: 14, lineHeight: 1.5,
                            }}>
                                {lastError}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <button onClick={handleConnect} disabled={busy} style={primaryBtnStyle}>
                                {!metaConnected ? t('connect') : t('reconnect')}
                            </button>
                            {metaConnected && (
                                <button
                                    onClick={() => handleDisconnect(facebook.platform, facebook.external_account_id)}
                                    disabled={busy}
                                    style={ghostBtnStyle}
                                >
                                    {t('disconnect')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderGoogleCard = () => {
        const googleConn = connections.find((c) => c.platform === 'google_gbp');
        const googleStatus = googleConn ? (googleConn.status || 'connected') : 'none';
        const googleBadge = badgeFor(googleStatus, t);
        const googleConnected = !!googleConn;
        const googlePending = googleStatus === 'pending_location';
        const googleNeedsReconnect = googleStatus === 'expired' || googleStatus === 'error';

        return (
            <div style={{
                background: C.white, border: `1px solid ${C.border}`, borderRadius: 14,
                padding: isMobile ? 20 : 24, maxWidth: 720,
            }}>
                <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 16,
                    flexDirection: isMobile ? 'column' : 'row',
                }}>
                    <div style={{
                        width: 48, height: 48, borderRadius: 12, flexShrink: 0,
                        background: C.white, border: `1.5px solid ${C.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <GoogleIcon />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
                            <span style={{ fontFamily: FONT, fontSize: 16, fontWeight: 800, color: C.text }}>
                                {t('google_business_profile')}
                            </span>
                            <span style={{
                                padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                                fontFamily: FONT, background: googleBadge.bg, color: googleBadge.color,
                                border: `1px solid ${googleBadge.border}`,
                            }}>
                                {googleBadge.label}
                            </span>
                        </div>

                        {googleConnected && googleConn.display_name && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: FONT, fontSize: 13, color: C.text, marginBottom: 14 }}>
                                <span style={{ fontWeight: 700, color: C.muted, minWidth: 84 }}>{t('google_business_profile')}</span>
                                <span style={{ fontWeight: 600 }}>{googleConn.display_name}</span>
                            </div>
                        )}

                        {googlePending && (
                            <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, marginBottom: 14 }}>
                                {t('gbp_finish_setup_hint')}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <button
                                onClick={googlePending ? openGooglePicker : handleGoogleConnect}
                                disabled={busy}
                                style={{ ...primaryBtnStyle, background: googleNeedsReconnect ? C.amber : PLATFORM_META.google.color }}
                            >
                                {googlePending ? t('select_location') : (!googleConnected ? t('connect') : t('reconnect'))}
                            </button>
                            {googleConnected && (
                                <button
                                    onClick={() => handleDisconnect(googleConn.platform, googleConn.external_account_id)}
                                    disabled={busy}
                                    style={ghostBtnStyle}
                                >
                                    {t('disconnect')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderEmptyState = () => (
        <div style={{
            maxWidth: 520, margin: '0 auto', textAlign: 'center',
            paddingTop: isMobile ? 24 : 48, paddingBottom: 32,
        }}>
            <div style={{
                width: 64, height: 64, background: 'linear-gradient(135deg, #4f46e5, #6366f1)',
                borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
            }}>
                <IconLink />
            </div>
            <h3 style={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, color: C.text, margin: '0 0 8px' }}>
                {t('no_connections_yet')}
            </h3>
            <p style={{ fontFamily: FONT, color: C.muted, fontSize: 14, lineHeight: 1.6, margin: '0 0 24px' }}>
                {t('no_connections_desc')}
            </p>
            <button onClick={handleConnect} disabled={busy} style={{
                padding: '11px 26px', borderRadius: 9, border: 'none',
                background: C.green, color: C.white, cursor: busy ? 'not-allowed' : 'pointer',
                fontFamily: FONT, fontSize: 13, fontWeight: 700, opacity: busy ? 0.6 : 1,
            }}>
                {t('connect_account_cta')}
            </button>
        </div>
    );

    const renderConnectionsBody = () => {
        if (error) {
            return (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <p style={{ fontFamily: FONT, color: C.red, fontSize: 14, margin: '0 0 16px' }}>
                        {t('failed_to_load_connections')}
                    </p>
                    <button onClick={loadConnections} style={{
                        padding: '10px 22px', borderRadius: 9, border: `1.5px solid ${C.border}`,
                        background: C.white, color: C.text, cursor: 'pointer',
                        fontFamily: FONT, fontSize: 13, fontWeight: 700,
                    }}>
                        {t('retry')}
                    </button>
                </div>
            );
        }
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {renderMetaCard()}
                {renderGoogleCard()}
            </div>
        );
    };

    const renderInsightsBody = () => {
        // No connection — guide the user to Connections
        if (connections.length === 0) {
            return renderEmptyState();
        }
        if (insightsLoading) {
            return (
                <div style={{ textAlign: 'center', padding: '60px 0', fontFamily: FONT, color: C.muted, fontSize: 14 }}>
                    {t('loading')}
                </div>
            );
        }

        const platforms = (insights && insights.sections && insights.sections.platforms) || {};
        const kpis = (insights && Array.isArray(insights.kpis)) ? insights.kpis : [];

        return (
            <div>
                {/* Refresh control */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 12 }}>
                    <button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        style={{
                            fontFamily: FONT, fontSize: 13, fontWeight: 700,
                            padding: '8px 16px', borderRadius: 8,
                            border: `1.5px solid ${C.green}`,
                            background: refreshing ? C.bg : C.white,
                            color: refreshing ? C.muted : C.green,
                            cursor: refreshing ? 'default' : 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: 8,
                        }}
                    >
                        <span style={{ fontSize: 14 }}>{'↻'}</span>
                        {refreshing ? t('refreshing') : t('refresh')}
                    </button>
                </div>

                {/* Refresh progress */}
                {refreshing && (
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ fontFamily: FONT, fontSize: 12.5, color: C.muted, marginBottom: 6 }}>
                            {t('fetching_latest_data')}
                        </div>
                        <div className="aqana-progress-track"><div className="aqana-progress-bar" /></div>
                    </div>
                )}

                {/* Date presets */}
                <div style={{ marginBottom: 24 }}>
                    <DateRangePicker
                        preset={preset} setPreset={setPreset}
                        startDate={customStart} setStartDate={setCustomStart}
                        endDate={customEnd} setEndDate={setCustomEnd}
                        t={t} isMobile={isMobile}
                    />
                </div>

                {/* Per-platform (Meta) */}
                <PlatformSection platformKey="instagram" platformData={platforms.instagram} kpis={kpis} isMobile={isMobile} t={t} />
                <PlatformSection platformKey="facebook" platformData={platforms.facebook} kpis={kpis} isMobile={isMobile} t={t} />

                {/* GBP section */}
                {hasGoogle && (
                    <GbpSection
                        discovery={gbpDiscovery}
                        reviews={gbpReviews}
                        keywords={gbpKeywords}
                        isMobile={isMobile}
                        t={t}
                    />
                )}

                {/* Top posts */}
                <div style={{ marginTop: 8 }}>
                    <div style={{
                        fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: 0.8, color: C.muted, marginBottom: 16,
                    }}>{t('top_posts')}</div>
                    <div style={{ background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 12, padding: isMobile ? 12 : 16 }}>
                        <TopPosts posts={topPosts} isMobile={isMobile} t={t} />
                    </div>
                </div>
            </div>
        );
    };

    // Primary section tabs: Social Media | Promotions — underline tab bar (matches Settings page)
    const renderSectionToggle = () => (
        <div style={{
            display: 'flex', gap: 0, borderBottom: `2px solid ${C.border}`,
            marginBottom: 24, overflowX: 'auto',
        }}>
            {[
                { key: 'social',      label: t('social_media') },
                { key: 'promotions',  label: t('promotions') },
            ].map((s) => {
                const isActive = section === s.key;
                return (
                    <button
                        key={s.key}
                        onClick={() => setSection(s.key)}
                        style={{
                            padding: '10px 18px', border: 'none', cursor: 'pointer', flexShrink: 0,
                            background: 'transparent', fontFamily: FONT, fontSize: 13, fontWeight: 600,
                            color: isActive ? C.green : C.muted,
                            borderBottom: `2px solid ${isActive ? C.green : 'transparent'}`,
                            marginBottom: -2, whiteSpace: 'nowrap',
                            transition: 'color 0.15s, border-color 0.15s',
                        }}
                    >{s.label}</button>
                );
            })}
        </div>
    );

    // Secondary view toggle (Insights | Connections) — shown only in Social section
    const renderViewToggle = () => (
        <div style={{
            display: 'inline-flex', background: C.bg, borderRadius: 9, border: `1.5px solid ${C.border}`,
            overflow: 'hidden', marginBottom: 20,
        }}>
            {[
                { key: 'insights', label: t('insights') },
                { key: 'connections', label: t('connections') },
            ].map((v) => (
                <button
                    key={v.key}
                    onClick={() => setView(v.key)}
                    style={{
                        fontFamily: FONT, fontSize: 13, fontWeight: 700,
                        padding: '8px 18px', border: 'none', cursor: 'pointer',
                        background: view === v.key ? C.green : 'transparent',
                        color: view === v.key ? C.white : C.muted, transition: 'all 0.15s',
                    }}
                >{v.label}</button>
            ))}
        </div>
    );

    return (
        <PortalLayout>
            <ToastContainer toasts={toasts} />

            {/* Google location picker modal */}
            {googleLocations !== null && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: 'fixed', inset: 0,
                        background: 'rgba(0,0,0,0.45)',
                        zIndex: 2000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 16,
                    }}
                    onClick={(e) => { if (e.target === e.currentTarget) setGoogleLocations(null); }}
                >
                    <div style={{
                        background: C.white, borderRadius: 14, padding: 24,
                        maxWidth: 480, width: '100%',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <h3 style={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, color: C.text, margin: 0 }}>
                                {t('select_location')}
                            </h3>
                            <button
                                onClick={() => setGoogleLocations(null)}
                                style={{
                                    background: 'transparent', border: 'none', cursor: 'pointer',
                                    color: C.muted, fontFamily: FONT, fontSize: 20, lineHeight: 1,
                                    padding: 4,
                                }}
                                aria-label={t('close')}
                            >
                                ×
                            </button>
                        </div>
                        {googleQuotaBlocked && (
                            <p style={{ fontFamily: FONT, fontSize: 13, color: C.muted, margin: '0 0 12px', lineHeight: 1.55 }}>
                                {t('gbp_pending_api_approval')}
                            </p>
                        )}
                        {googleLocations.length === 0 && !googleQuotaBlocked && (
                            <p style={{ fontFamily: FONT, fontSize: 13, color: C.muted, margin: 0 }}>
                                {t('no_locations_found')}
                            </p>
                        )}
                        {googleLocations.map((loc) => (
                            <button
                                key={loc.location_id}
                                onClick={() => handleSelectLocation(loc)}
                                style={{
                                    display: 'block', width: '100%', textAlign: 'left',
                                    padding: 12, border: `1px solid ${C.border}`, borderRadius: 8,
                                    marginTop: 8, background: C.white, cursor: 'pointer',
                                    fontFamily: FONT, fontSize: 13, color: C.text,
                                    transition: 'border-color 0.15s',
                                }}
                            >
                                {loc.title || loc.location_id}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            <div style={{ fontFamily: FONT }}>
                {/* Primary tabs at the top (matches Settings flow; PortalLayout already pads the content) */}
                {renderSectionToggle()}

                <SectionHeader
                    title={t('marketing')}
                    description={
                        section === 'promotions'
                            ? t('promotions_desc')
                            : view === 'insights'
                                ? t('insights_desc')
                                : t('connections_desc')
                    }
                />

                {section === 'promotions' ? (
                    <PromotionsTab />
                ) : (
                    <>
                        {/* Secondary toggle (Social section) */}
                        {renderViewToggle()}

                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '60px 0', fontFamily: FONT, color: C.muted, fontSize: 14 }}>
                                {t('loading')}
                            </div>
                        ) : view === 'connections' ? (
                            <>
                                <div style={{
                                    display: 'flex', gap: 10, alignItems: 'flex-start',
                                    background: C.greenLight, border: `1px solid ${C.greenBorder}`,
                                    borderRadius: 10, padding: '10px 14px', marginBottom: 20, maxWidth: 760,
                                }}>
                                    <span style={{ fontSize: 14, flexShrink: 0, lineHeight: 1.5 }}>{'ℹ️'}</span>
                                    <div style={{ fontFamily: FONT, fontSize: 12.5, color: C.text, lineHeight: 1.5 }}>
                                        {t('connections_ig_prereq')}
                                    </div>
                                </div>
                                {renderConnectionsBody()}
                            </>
                        ) : (
                            renderInsightsBody()
                        )}
                    </>
                )}
            </div>
        </PortalLayout>
    );
};

export default MarketingPage;
